import React, { useEffect, useMemo, useState } from 'react';
import {
  DropDownTree,
  DropDownTreeExpandEvent,
  DropDownTreeFilterChangeEvent
} from '@progress/kendo-react-dropdowns';
import { FilterDescriptor } from '@progress/kendo-data-query';
import {
  extendDataItem,
  filterBy,
  mapTree
} from '@progress/kendo-react-treelist';
// ToDO can we type the compoenent so categories is typed? and item
type Props = {
  id?: string;
  dataTestid?: string;
  categories: object[];
  categoryClicked(id: string, index: string, name: string, item: object): void;
  selectedIndex?: string;
  selectedId?: string;
  className?: string;
  dataItemKey?: string;
  textField?: string;
  subItemsField?: string;
  placeholder?: string;
  defaultItemId?: string;
  disabled?: boolean;
  showFilter?: boolean;
  filterThreshold?: number;
};

const selectField = 'selected';
const expandField = 'expanded';

const countCategories = (
  categories: object[] | null | undefined,
  subItemsField: string
): number => {
  if (categories?.length === 0) {
    return 0;
  }

  let count = 0;
  for (const category of categories) {
    count += 1;
    const children = category[subItemsField];
    if (children && children.length > 0) {
      count += countCategories(children, subItemsField);
    }
  }

  return count;
};

const KendoReactDropDownTree = (props: Props) => {
  const {
    id,
    dataTestid,
    categories,
    categoryClicked,
    selectedId,
    className,
    dataItemKey = 'id',
    textField = 'text',
    subItemsField = 'items',
    placeholder = 'All Categories',
    defaultItemId,
    disabled = false,
    showFilter = false,
    filterThreshold = 0
  } = props;
  const fields = { selectField, expandField, dataItemKey, subItemsField };

  const totalCategories = useMemo(() => {
    return showFilter ? countCategories(categories, subItemsField) : 0;
  }, [categories, showFilter, subItemsField]);

  const effectiveShowFilter = showFilter && totalCategories > filterThreshold;

  const [value, setValue] = useState(null);
  const [expanded, setExpanded] = React.useState([]);
  const [filter, setFilter] = React.useState<FilterDescriptor>({
    value: '',
    operator: 'contains'
  });

  useEffect(() => {
    const findItem = (item, id) => {
      if (item[dataItemKey] === id) {
        return item;
      }
      if (item[subItemsField]) {
        for (let i = 0; i < item[subItemsField].length; i++) {
          const found = findItem(item[subItemsField][i], id);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    let selectedItem;
    for (const item of categories) {
      selectedItem = findItem(item, selectedId);
      if (selectedItem) {
        break;
      }
    }
    if (selectedItem) {
      setValue(selectedItem);
    }
  }, [selectedId, categories]);

  const onChange = (event) => {
    const selectedValue = event.value;

    setValue(selectedValue);

    // TODO this should only return the whole item
    categoryClicked(
      selectedValue == null ? defaultItemId : selectedValue[dataItemKey],
      null,
      selectedValue == null ? null : selectedValue[textField],
      selectedValue
    );
  };

  const onExpandChangeDropdown = React.useCallback(
    (event: DropDownTreeExpandEvent) =>
      setExpanded(expandedState(event.item, dataItemKey, expanded)),
    [expanded]
  );

  const processTreeData = (data, state, fields) => {
    const { selectField, expandField, dataItemKey, subItemsField } = fields;
    const { expanded, value, filter } = state;
    const filtering = Boolean(filter && filter.value);

    return mapTree(
      filtering ? filterBy(data, [filter], subItemsField) : data,
      subItemsField,
      (item) => {
        const props = {
          [expandField]: filtering
            ? true
            : expanded.includes(item[dataItemKey]),
          [selectField]: value && item[dataItemKey] === value[dataItemKey]
        };
        return filtering
          ? extendDataItem(item, subItemsField, props)
          : { ...item, ...props };
      }
    );
  };

  const treeData = React.useMemo(
    () => processTreeData(categories, { expanded, value, filter }, fields),
    [expanded, value, filter, categories]
  );

  const expandedState = (item, dataItemKey, expanded) => {
    const nextExpanded = expanded.slice();
    const itemKey = item[dataItemKey];
    const index = expanded.indexOf(itemKey);
    index === -1 ? nextExpanded.push(itemKey) : nextExpanded.splice(index, 1);

    return nextExpanded;
  };

  const onNameFilterChange = (event: DropDownTreeFilterChangeEvent) => {
    setFilter(event.filter);
  };

  return (
    <div className="kendo-react-dropdowntree" data-testid={dataTestid}>
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <DropDownTree
        id={id}
        placeholder={placeholder}
        data={treeData}
        onChange={onChange}
        value={value}
        textField={textField}
        subItemsField={subItemsField}
        dataItemKey={dataItemKey}
        expandField={expandField}
        disabled={disabled}
        onExpandChange={onExpandChangeDropdown}
        filterable={effectiveShowFilter}
        filter={filter.value}
        onFilterChange={onNameFilterChange}
        popupSettings={{
          className: `kendo-react-dropdowntree-popup ${className}`,
          animate: false
        }}
        className={value ? '' : 'no-value'}
      />
    </div>
  );
};

export default KendoReactDropDownTree;
